import { X } from 'lucide-react'

import { PieChart, PieChartPie } from '@/pages/ui/pie-chart'

import { Button } from '@/components/button/button'
import { SelectFilter } from '@/components/select-filter/select-filter'
import type { SelectFilterItem } from '@/components/select-filter/types'

import { cn } from '@/lib/utils'

import { CaseTypeBreakdownLegend } from './legend'
import { CaseTypeBreakdownTooltip } from './tooltip'
import { useCaseTypeBreakdown } from './use-case-type-breakdown'

interface Props {
  className?: string
  assignees: SelectFilterItem[]
  tags: SelectFilterItem[]
}

export const CaseTypeBreakdown: React.FC<Props> = ({ className, assignees, tags }) => {
  const {
    selectedAssignees,
    setSelectedAssignees,
    selectedTags,
    setSelectedTags,
    selectedActiveInactive,
    setSelectedActiveInactive,
    selectedTagCondition,
    setSelectedTagCondition,
    isFetchingCaseType,
    chartDataNormalized,
    chartDataSmall,
    onFilterReset,
    onCaseTypeClick,
  } = useCaseTypeBreakdown()

  return (
    <div className={cn('px-5 py-6', 'rounded-lg border border-border', 'shadow-sm', className)}>
      {/* FILTERS */}
      <div className={cn('mb-8', 'flex gap-2.5')}>
        <SelectFilter
          buttonText="Assignee"
          hideCount
          disabled={!assignees.length || isFetchingCaseType}
          values={selectedAssignees}
          items={assignees}
          onChangeValue={setSelectedAssignees}
        />
        <SelectFilter
          buttonText="Tag"
          hideCount
          showsFilterOption
          disabled={!tags.length || isFetchingCaseType}
          filterOption={selectedTagCondition}
          values={selectedTags}
          items={tags}
          onChangeValue={setSelectedTags}
          onChangeFilterOption={setSelectedTagCondition}
        />
        <SelectFilter
          buttonText="Active/Inactive"
          hideCount
          disabled={isFetchingCaseType}
          values={selectedActiveInactive}
          items={[
            { filterName: 'Active', filterValue: 'active', count: 0 },
            { filterName: 'Inactive', filterValue: 'inactive', count: 0 },
          ]}
          onChangeValue={setSelectedActiveInactive}
        />
        {!!(selectedAssignees.length || selectedTags.length || selectedActiveInactive.length) && (
          <Button className={cn('flex items-center gap-2 px-4')} variant="ghost" size="sm" onClick={onFilterReset}>
            Reset
            <X size={16} />
          </Button>
        )}
      </div>

      {/* HEADING */}
      <div className={cn('text-[18px] font-[600] leading-[28px]')}>Case Type Breakdown</div>

      {/* CHART */}
      <div className={cn('flex justify-center')}>
        <div
          style={{
            transition: 'opacity 120ms ease-out',
            opacity: isFetchingCaseType ? 0.7 : 1,
          }}
        >
          <PieChart width={700} height={308 + 3 * 30}>
            <PieChartPie data={chartDataNormalized} outerRadius={154} innerRadius={80.5} />
            <CaseTypeBreakdownTooltip
              data={chartDataNormalized}
              dataOthers={chartDataSmall}
              onClick={onCaseTypeClick}
            />
          </PieChart>
          <CaseTypeBreakdownLegend data={chartDataNormalized} />
        </div>
      </div>
    </div>
  )
}
